import React, { useState, useEffect } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import BEMHelper from 'react-bem-helper'
import { Formik, Form } from 'formik'
import { useConnect } from 'redux-bundler-hook'
import {
	Button,
	Checkbox,
	FormControlLabel,
	makeStyles,
} from '@material-ui/core/'
import { get } from 'lodash'
import { TextField } from '@material-ui/core/'
import useTranslations from '@src/hooks/useTranslations'
import validator from '@src/form/validations'
import FormContainer from '@src/components/FormContainer'
import Email from '@src/components/account/EmailField'
import Password from '@src/components/account/PasswordField'
import Phonenumber from '@src/components/account/Phonenumber'
import NameField from '@src/components/account/NameField'
import {
	azureApiGatewayPost,
	remoteLogError,
} from '@src/api/azureApiGateway/client'
import { googleAnalytics } from '@src/api/googleanalytics/googleAnalytics'
import Consent from '@src/components/Consent'
import { REGISTRATION_STEPS } from '@src/components/account/Account'
import useSetting from '@src/hooks/useSetting'

const bem = new BEMHelper('registration-page')

const useStyles = makeStyles({
	adornment: {
		'& > p': {
			fontSize: '.75rem',
		},
	},
	formLabel: {
		display: 'flex',
		marginRight: 9,
	},
})

const query = graphql`
	query getConsents {
		allPolicyDocument {
			edges {
				node {
					id
					title
					url
					documentVersion
				}
			}
		}
		allSnippet {
			edges {
				node {
					editables {
						name
						type
						value
					}
					name
				}
			}
		}
	}
`

function ContactInformation({ setNextStep, setLoading, setError }) {
	const classes = useStyles()
	const [state, setState] = useState({})
	const [snippetValues, setSnippetValues] = useState(null)
	const [source, setSource] = useState('')
	const [medium, setMedium] = useState('')
	const [showBusinessField, setShowBusinessField] = useState(false)
	const [businessCodeValue, setBusinessCodeValue] = useState('')
	const { allPolicyDocument, allSnippet } = useStaticQuery(query)
	const getSetting = useSetting()
	const gtmId = getSetting('GTM_ID')

	useEffect(() => {
		const url =
			typeof window !== 'undefined' &&
			window.google_tag_manager[gtmId] !== undefined &&
			window.google_tag_manager[gtmId].dataLayer !== undefined &&
			window.google_tag_manager[gtmId].dataLayer.get('originalLocation') !==
				undefined
				? new URL(
						window.google_tag_manager[gtmId].dataLayer.get('originalLocation'),
				  )
				: ''

		const params = new URLSearchParams(url.search)

		setMedium(params.get('utm_medium') !== null ? params.get('utm_medium') : '')
		setSource(
			params.get('utm_source') !== null
				? params.get('utm_source')
				: document.referrer,
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const setupAcceptPolicyDocuments = () => {
		const acceptedPolicies = {}
		const snippetIds = allPolicyDocument.edges.map(({ node }) => {
			acceptedPolicies[node.id] = false
			return buildSnippetKey(node)
		})

		setState(acceptedPolicies)

		const matchingNodes = allSnippet.edges.reduce(
			(nodesWithSnippetThatMatches, node) => {
				node.node.editables.forEach((editable) => {
					for (let snippetId in snippetIds) {
						if (editable.value === snippetIds[snippetId]) {
							nodesWithSnippetThatMatches.push(node.node)
						}
					}
				})
				return nodesWithSnippetThatMatches
			},
			[],
		)

		const snippetValuesResult = matchingNodes.reduce((result, node) => {
			var localSnippetValues = []
			node.editables.forEach((editable) => {
				localSnippetValues[editable.name] = editable.value
			})
			result[localSnippetValues.policyDocument] = localSnippetValues
			return result
		}, [])
		setSnippetValues(snippetValuesResult)
	}

	const t = useTranslations()
	const { doSetCustomerInfo, customerInfo, doLoginCustomer } = useConnect(
		'doSetCustomerInfo',
		'selectCustomerInfo',
		'doLoginCustomer',
	)

	useEffect(() => {
		setupAcceptPolicyDocuments()
		googleAnalytics.pushVirtualPageView(
			`/virtual/account/contactinformation`,
			`User is typing in contact information`,
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (
			sessionStorage.getItem('b2b-invite') !== null &&
			sessionStorage.getItem('b2b-invite') === 'showField'
		) {
			setShowBusinessField(true)
		} else if (sessionStorage.getItem('b2b-invite') !== null) {
			setBusinessCodeValue(sessionStorage.getItem('b2b-invite'))
		} else {
			setBusinessCodeValue('notBusiness')
		}
	}, [])

	const checkPolicyDocument = (nodeId, value) => {
		const values = { ...state }
		values[nodeId] = value
		setState(values)
	}

	const hasAcceptedAllDocuments = () => {
		for (let nodeId in state) {
			if (!state[nodeId]) {
				return false
			}
		}
		return true
	}

	async function createCustomer(userData) {
		const policy_document_agreements = []
		for (let documentId in state) {
			allPolicyDocument.edges.filter(({ node }) => {
				if (node.id === documentId) {
					policy_document_agreements.push({
						policy_document: documentId,
						document_version: node.documentVersion,
					})
				}
			})
		}
		try {
			setLoading(true)

			let customerInformation = {
				first_name: userData.firstName,
				last_name: userData.lastName,
				email: userData.email,
				password: userData.password,
				phone_number: userData.phonenumber,
				policy_document_agreements,
				consent_decisions: [],
				source_url: source,
				medium: medium,
			}
			if (typeof sessionStorage.getItem('id') === 'string') {
				customerInformation.external_membership_number = sessionStorage.getItem(
					'id',
				)
				if (typeof sessionStorage.getItem('linksource') === 'string') {
					customerInformation.raf_source = sessionStorage.getItem('linksource')
				}
			}

			if (userData.businessCode !== 'notBusiness') {
				customerInformation.business_profile_activation_code =
					userData.businessCode
			}

			const response = await azureApiGatewayPost(
				'customer',
				customerInformation,
				{
					params: { registration: true },
				},
			)
			doSetCustomerInfo({
				customerId: response.data.id,
				firstName: userData.firstName,
				lastName: userData.lastName,
				email: userData.email,
				phonenumber: userData.phonenumber,
			})
			await doLoginCustomer(userData.email, userData.password, true)
			sessionStorage.removeItem('b2b-invite')
			setNextStep(REGISTRATION_STEPS.VERIFY_MOBILE_NUMBER)
			setLoading(false)
		} catch (error) {
			setLoading(false)
			if (get(error, 'response.status') && error.response.status === 400) {
				if (get(error, 'response.data.messages.length') > 0) {
					if (
						error.response.data.messages.includes(
							'apigw.error.create.customer.already.exists.correct.password',
						) ||
						error.response.data.messages.includes(
							'apigw.error.create.customer.already.exists.wrong.password',
						)
					) {
						setError({
							message: error.response.data.messages[0],
							buttonLabel: t('kinto.registration.go-to-login'),
							redirectOnClose: true,
						})
						return
					} else if (
						error.response.data.messages.includes(
							'apigw.error.create.customer.no.message.available',
						)
					) {
						setError({
							message: error.response.data.messages[0],
							buttonLabel: t('kinto.general.close'),
							redirectOnClose: false,
						})
						return
					} else {
						setError({
							message: error.response.data.messages.join(', '),
							buttonLabel: t('kinto.general.close'),
							redirectOnClose: false,
						})
						return
					}
				}
			}
			setError({
				message: 'apigw.error.create.customer.no.message.available',
				buttonLabel: t('kinto.general.close'),
				redirectOnClose: false,
			})
			remoteLogError(error.message)
		}
	}

	return (
		<div {...bem('')}>
			<Formik
				enableReinitialize
				initialValues={{
					firstName:
						customerInfo && customerInfo.firstName
							? customerInfo.firstName
							: '',
					lastName:
						customerInfo && customerInfo.lastName ? customerInfo.lastName : '',
					phonenumber:
						customerInfo && customerInfo.phonenumber
							? customerInfo.phonenumber
							: '',
					email: customerInfo && customerInfo.email ? customerInfo.email : '',
					secondEmail:
						customerInfo && customerInfo.email ? customerInfo.email : '',
					password: '',
					businessCode: businessCodeValue !== '' ? businessCodeValue : '',
				}}
				validationSchema={validator({
					firstName: {
						validatorKey: 'name',
						required: true,
					},
					lastName: {
						validatorKey: 'name',
						required: true,
					},
					phonenumber: {
						validatorKey: 'phonenumber',
						required: true,
					},
					email: {
						validatorKey: 'email',
						required: true,
					},
					secondEmail: {
						validatorKey: 'secondEmail',
						required: true,
					},
					password: {
						validatorKey: 'password',
						required: true,
					},
					businessCode: {
						validatorKey: 'businessCode',
						required: true,
					},
				})}
			>
				{(props) => {
					const { values, dirty, isValid } = props
					const disabled = !(isValid && dirty && hasAcceptedAllDocuments())
					return (
						<Form>
							<FormContainer twoColumns>
								<NameField label={t('kinto.form.firstname')} name='firstName' />
								<NameField label={t('kinto.form.lastname')} name='lastName' />
								<Phonenumber {...props} />
								<Email {...props} />
								<TextField
									label={t('kinto.form.second-email')}
									id={t('kinto.form.second-email')}
									variant='outlined'
									name='secondEmail'
									required
									onChange={props.handleChange}
									onBlur={props.handleBlur}
									error={props.errors.secondEmail && props.touched.secondEmail}
									helperText={
										props.errors.secondEmail &&
										props.touched.secondEmail &&
										props.errors.secondEmail
									}
								/>
								<Password {...props} />
								{showBusinessField && (
									<TextField
										label={t('kinto.form.business-code')}
										id={t('kinto.form.business-code')}
										variant='outlined'
										name='businessCode'
										required
										onChange={props.handleChange}
										onBlur={props.handleBlur}
										error={
											props.errors.businessCode && props.touched.businessCode
										}
										helperText={
											props.errors.businessCode &&
											props.touched.businessCode &&
											props.errors.businessCode
										}
									/>
								)}
							</FormContainer>
							<div {...bem('checkboxes')}>
								{allPolicyDocument.edges.map(({ node }, index) => (
									<FormControlLabel
										key={`policydoc-${node.id}`}
										className={classes.formLabel}
										control={
											<Checkbox
												className={classes.checkbox}
												checked={!!state[node.id]}
												onChange={(event) =>
													checkPolicyDocument(node.id, event.target.checked)
												}
												name='userPolicy'
												color='primary'
											/>
										}
										label={t(`kinto.register.policydocument-${node.id}`, [
											snippetValues && (
												<Consent
													key={`content-checkbox-${node.id}-${index}`}
													header={
														snippetValues[buildSnippetKey(node)]['header']
													}
													linkText={
														snippetValues[buildSnippetKey(node)]['linkText']
													}
													content={
														snippetValues[buildSnippetKey(node)]['content']
													}
												></Consent>
											),
										])}
									/>
								))}
							</div>

							<FormContainer>
								<Button
									aria-label={t('kinto.general.next')}
									variant='contained'
									color='primary'
									fullWidth
									disabled={disabled}
									onClick={() => {
										createCustomer(values)
									}}
								>
									{t('kinto.general.next')}
								</Button>
							</FormContainer>
						</Form>
					)
				}}
			</Formik>
		</div>
	)
}

export default ContactInformation

function buildSnippetKey(node) {
	return `id${node.id}version${node.documentVersion}`
}
